<template>
  <el-dialog :title="$t('message.cashbackCollectionDetails')" :visible.sync="dialogVisible"
    :close-on-click-modal="false" width="1000px" top="8vh" append-to-body custom-class="purchase-cashback-dialog">
    <!-- 当前采购交易号支持快捷复制 -->
    <div class="purchase-info">
      <span>{{ $t('message.transactionNumber') }}：{{ datas.purchase_no }}</span>
      <el-button type="text" size="mini" @click="copy(datas.purchase_no)">{{ $t('message.copy') }}</el-button>
      <!-- 782：创建采购返现收款，同时遵守列表接口返回的创建限制。 -->
      <el-button v-if="isAuth(782) && canCreate" type="success" size="small" class="create-button" :disabled="loading"
        @click="$refs.purchaseCashbackCreate.open(datas.purchase_no)">
        {{ $t('message.cashbackCreateLink') }}
      </el-button>
    </div>
    <!-- 收款链接明细：链接、金额、到账状态和收款原因 -->
    <el-table v-loading="loading" :data="tableData" row-key="id" border
      :header-cell-style="{ background: '#EDEFF4' }" max-height="480" style="width: 100%">
      <el-table-column :label="$t('message.link')" min-width="360">
        <template slot-scope="scope">
          <div class="collection-link">
            <a :href="scope.row.url" target="_blank" rel="noopener noreferrer">{{ scope.row.url }}</a>
            <el-button type="text" size="mini" @click="copy(scope.row.url)">{{ $t('message.copy') }}</el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="amount" :label="$t('message.cashbackAmountYuan')" width="120" align="center" />
      <el-table-column :label="$t('message.status')" width="120" align="center">
        <template slot-scope="scope">
          <span :class="{ paid: scope.row.status === 'paid', pending: scope.row.status === 'pending' }">
            {{ scope.row.status_name }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('message.cashbackCollectionReason')" min-width="200" align="center">
        <template slot-scope="scope">
          <!-- 收款原因仅作纯文本展示，保留换行。 -->
          <span class="collection-reason">{{ scope.row.reason }}</span>
        </template>
      </el-table-column>
      <!-- 783：作废采购返现收款，仅未到账且接口允许废弃的主单可操作。 -->
      <el-table-column v-if="isAuth(783) && canVoid" :label="$t('message.operation')" width="80" align="center">
        <template slot-scope="scope">
          <el-button v-if="scope.row.status === 'pending' && scope.row.can_void === true"
            type="text" icon="el-icon-delete" class="void-button" :title="$t('message.delete')"
            :aria-label="$t('message.delete')" :loading="voidingId === scope.row.id"
            :disabled="loading || voidingId !== null" @click="voidCashback(scope.row)" />
        </template>
      </el-table-column>
    </el-table>
    <!-- 请求失败时保留当前查询条件，允许重试 -->
    <div v-if="loadFailed" class="load-error">
      <span>{{ $t('message.cashbackListLoadFailed') }}</span>
      <el-button type="text" @click="getList">{{ $t('message.cashbackListRetry') }}</el-button>
    </div>
    <!-- 默认每页 20 条，接口允许的最大每页数量为 100 -->
    <div class="pagination">
      <el-pagination background :current-page="datas.page" :page-size="datas.pageSize"
        :page-sizes="[20, 50, 100]" :total="total" :disabled="loading"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
    <!-- 独立创建表单通过事件通知本组件刷新列表。 -->
    <purchaseCashbackCreate ref="purchaseCashbackCreate" @created="handleCreated" />
  </el-dialog>
</template>

<script>
import purchaseCashbackCreate from './purchaseCashbackCreate'

export default {
  components: { purchaseCashbackCreate },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      loadFailed: false,
      tableData: [],
      total: 0,
      canCreate: false,
      canVoid: false,
      // 从确认提示到接口完成均锁定操作，避免重复弹框或提交。
      voidingId: null,
      // 请求序号用于防止旧请求覆盖最新交易号或分页的结果。
      requestId: 0,
      // 收款模块仅按采购交易号查询。
      datas: {
        purchase_no: '',
        page: 1,
        pageSize: 20,
      },
    }
  },
  methods: {
    // 由列表页通过 ref 打开，每次打开重置分页并重新查询。
    open(purchaseNo) {
      if (!this.isAuth(781)) return
      this.datas = {
        // 长交易号不能转成 Number，否则会丢失精度。
        purchase_no: String(purchaseNo),
        page: 1,
        pageSize: 20,
      }
      this.tableData = []
      this.total = 0
      this.canCreate = false
      this.canVoid = false
      this.dialogVisible = true
      this.getList()
    },
    // 普通打开及翻页只查询明细，不通知父页重查摘要，避免一次点击请求两次。
    async getList() {
      if (!this.isAuth(781)) return
      const requestId = ++this.requestId
      this.loading = true
      this.loadFailed = false
      this.tableData = []
      try {
        const res = await this.$api.problemApi.purchaseCashbackList({ ...this.datas })
        // 切换交易号后，忽略上一次请求的响应。
        if (requestId !== this.requestId) return
        if (res.code !== 0) {
          this.loadFailed = true
          return
        }
        this.tableData = res.data.list
        this.total = res.data.total
        this.canCreate = res.data.can_create === true
        this.canVoid = res.data.can_void === true
      } catch (error) {
        if (requestId === this.requestId) this.loadFailed = true
      } finally {
        // 旧请求完成时不能关闭新请求的加载状态。
        if (requestId === this.requestId) this.loading = false
      }
    },
    // 废弃使用收款主单 id，不使用 payments 中的支付尝试 id。
    async voidCashback(row) {
      if (!this.isAuth(783)) return
      if (this.loading || this.voidingId !== null || !this.canVoid || row.status !== 'pending' || row.can_void !== true) return
      const purchaseNo = this.datas.purchase_no
      this.voidingId = row.id
      try {
        await this.$confirm(this.$t('message.cashbackVoidMessage'), this.$t('message.cashbackVoidTitle'), {
          confirmButtonText: this.$t('message.cashbackConfirmDelete'),
          cancelButtonText: this.$t('message.cancel'),
          type: 'warning',
          center: true,
          customClass: 'purchase-cashback-void-confirm',
        })
        try {
          const res = await this.$api.problemApi.purchaseCashbackVoid({ id: row.id })
          if (res.code === 0) this.$message.success(res.msg)
        } finally {
          // 失败或超时也重新查询，核对是否已废弃或已到账；不刷新其他交易号。
          if (this.datas.purchase_no === purchaseNo) {
            await this.getList()
            // 废弃操作可能改变到账状态，仅记录变更后重查父页摘要。
            this.$emit('latest-change', { purchase_no: purchaseNo })
          }
        }
      } catch (error) {
        // 取消确认不发请求，接口错误使用公共请求层的提示。
      } finally {
        this.voidingId = null
      }
    },
    // 新建成功后回到第一页，避免分页导致刚创建的链接不可见。
    handleCreated(purchaseNo) {
      if (purchaseNo !== this.datas.purchase_no) return
      this.datas.page = 1
      this.getList()
      // 创建成功后同步父页金额摘要，普通查看和翻页不触发。
      this.$emit('latest-change', { purchase_no: purchaseNo })
    },
    // 切换每页数量后从第一页查询，避免原页码超出范围。
    handleSizeChange(size) {
      this.datas.pageSize = size
      this.datas.page = 1
      this.getList()
    },
    // 翻页时沿用当前交易号和每页数量。
    handleCurrentChange(page) {
      this.datas.page = page
      this.getList()
    },
  },
}
</script>

<style lang="scss" scoped>
.purchase-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;

  .create-button {
    margin-left: auto;
  }
}

.collection-reason {
  white-space: pre-wrap;
  word-break: break-word;
}

.collection-link {
  display: flex;
  align-items: center;
  gap: 10px;

  a {
    flex: 1;
    min-width: 0;
    word-break: break-all;
    color: #409EFF;
  }
}

.paid {
  color: #67C23A;
}

.pending {
  color: #F56C6C;
}

.void-button {
  color: #F56C6C;
  font-size: 22px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.load-error {
  color: #F56C6C;
  margin-top: 10px;

  .el-button {
    margin-left: 12px;
  }
}
</style>

<style>
/* 弹框挂载到 body，使用独立类名限制窄屏宽度。 */
.purchase-cashback-dialog {
  max-width: calc(100% - 40px);
}

/* 仅调整废弃提示正文的颜色和对齐，沿用项目默认确认框样式。 */
.purchase-cashback-void-confirm .el-message-box__message {
  color: #F56C6C;
  text-align: center;
}

</style>
